import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

// Comprehensive Salesforce Environment Management Controller
export default class extends Controller {
  static targets = [
    // Main page targets
    "activeEnvironment", "totalCount", "environmentsList", 
    "typeFilter", "statusFilter", "environmentStatus",
    
    // Environment modal targets
    "environmentModal", "environmentForm", "environmentId", 
    "environmentName", "environmentType", "environmentDescription",
    "clientId", "clientSecret", "username", "password", "securityToken",
    "instanceUrl", "isSandbox", "autoSyncEnabled", "testOnSave",
    "modalTitle", "saveButton", "saveButtonText", "saveSpinner",
    
    // Field mappings modal targets
    "fieldMappingsModal", "mappingEnvironmentName", "objectType",
    "mappingsTable", "emptyMappingsRow", "mappingRowTemplate",
    "activeMappingsCount", "validationWarnings", "validationWarningsList",
    "saveMappingsButton", "saveMappingsButtonText", "saveMappingsSpinner",
    
    // Import modal targets
    "importModal", "importForm", "dropZone", "importFile",
    "selectedFileInfo", "fileName", "fileSize",
    "overwriteExisting", "importFieldMappings", "validateOnImport", "testConnections",
    "previewSection", "previewContent", "validationResults", "validationContent",
    "progressSection", "progressBar", "progressText",
    "previewButton", "importButton", "importButtonText", "importSpinner",
    
    // Confirmation modal targets
    "confirmationModal", "confirmationIcon", "confirmationTitle", "confirmationMessage",
    "confirmationDetails", "destructiveWarning", "destructiveWarningText",
    "confirmationInput", "confirmationInputValue", "confirmationInputField",
    "confirmButton", "confirmButtonText", "confirmSpinner",
    
    // Template targets
    "deleteEnvironmentTemplate", "switchEnvironmentTemplate", "testConnectionTemplate"
  ]

  connect() {
    console.log("Salesforce Environments controller connected")
    this.currentEnvironmentId = null
    this.pendingAction = null
    this.currentMappings = []
    this.selectedFile = null
    this.initializeDropZone()
  }

  // ========================================
  // Environment Management Actions
  // ========================================

  openAddModal() {
    this.resetEnvironmentForm()
    this.modalTitleTarget.textContent = "Add Salesforce Environment"
    this.saveButtonTextTarget.textContent = "Save Environment"
    this.showModal(this.environmentModalTarget)
  }

  editEnvironment(event) {
    const environmentId = event.currentTarget.dataset.environmentId
    this.loadEnvironmentData(environmentId)
    this.modalTitleTarget.textContent = "Edit Salesforce Environment"
    this.saveButtonTextTarget.textContent = "Update Environment"
    this.showModal(this.environmentModalTarget)
  }

  async saveEnvironment(event) {
    event.preventDefault()
    
    const formData = new FormData(this.environmentFormTarget)
    const environmentId = this.environmentIdTarget.value

    this.setLoading(this.saveButtonTarget, this.saveSpinnerTarget, this.saveButtonTextTarget, "Saving...")

    try {
      const url = environmentId ? 
        `/admin/salesforce/environments/${environmentId}` : 
        '/admin/salesforce_environments'
      
      const method = environmentId ? 'PATCH' : 'POST'

      const response = await fetch(url, {
        method: method,
        headers: {
          'X-CSRF-Token': this.getCSRFToken(),
          'Accept': 'application/json'
        },
        body: formData
      })

      const result = await response.json()

      if (response.ok) {
        this.showNotification('success', result.message || 'Environment saved successfully')
        this.closeModal()
        this.refreshEnvironmentsList()
        
        // Test connection if requested
        if (this.testOnSaveTarget.checked && result.environment_id) {
          await this.testEnvironmentConnection(result.environment_id)
        }
      } else {
        this.showNotification('error', result.error || 'Failed to save environment')
      }
    } catch (error) {
      console.error('Save environment error:', error)
      this.showNotification('error', 'Network error occurred while saving')
    } finally {
      this.clearLoading(this.saveButtonTarget, this.saveSpinnerTarget, this.saveButtonTextTarget, "Save Environment")
    }
  }

  deleteEnvironment(event) {
    const environmentId = event.currentTarget.dataset.environmentId
    const environmentRow = event.currentTarget.closest('tr')
    const environmentName = environmentRow.querySelector('td .text-gray-900').textContent.trim()
    const environmentType = environmentRow.querySelector('.inline-flex').textContent.trim()

    this.showConfirmation('delete', {
      environmentId,
      environmentName,
      environmentType,
      title: 'Delete Environment',
      message: 'This will permanently delete the Salesforce environment configuration.',
      destructive: true,
      confirmText: environmentName
    })
  }

  async setDefault(event) {
    const environmentId = event.currentTarget.dataset.environmentId
    
    try {
      const response = await fetch(`/admin/salesforce/environments/${environmentId}/set_default`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.getCSRFToken(),
          'Accept': 'application/json'
        }
      })

      const result = await response.json()

      if (response.ok) {
        this.showNotification('success', 'Default environment updated successfully')
        this.refreshEnvironmentsList()
      } else {
        this.showNotification('error', result.message || 'Failed to set default environment')
      }
    } catch (error) {
      console.error('Set default error:', error)
      this.showNotification('error', 'Network error occurred')
    }
  }

  async activate(event) {
    const environmentId = event.currentTarget.dataset.environmentId
    
    try {
      const response = await fetch(`/admin/salesforce/environments/${environmentId}/activate`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.getCSRFToken(),
          'Accept': 'application/json'
        }
      })

      const result = await response.json()

      if (response.ok) {
        this.showNotification('success', 'Environment activated successfully')
        this.refreshEnvironmentsList()
      } else {
        this.showNotification('error', result.message || 'Failed to activate environment')
      }
    } catch (error) {
      console.error('Activate error:', error)
      this.showNotification('error', 'Network error occurred')
    }
  }

  async deactivate(event) {
    const environmentId = event.currentTarget.dataset.environmentId
    
    try {
      const response = await fetch(`/admin/salesforce/environments/${environmentId}/deactivate`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.getCSRFToken(),
          'Accept': 'application/json'
        }
      })

      const result = await response.json()

      if (response.ok) {
        this.showNotification('success', 'Environment deactivated successfully')
        this.refreshEnvironmentsList()
      } else {
        this.showNotification('error', result.message || 'Failed to deactivate environment')
      }
    } catch (error) {
      console.error('Deactivate error:', error)
      this.showNotification('error', 'Network error occurred')
    }
  }

  async switchEnvironment(event) {
    const environmentId = event.currentTarget.dataset.environmentId
    const environmentRow = event.currentTarget.closest('tr')
    const newEnvironmentName = environmentRow.querySelector('td .text-gray-900').textContent.trim()
    const newEnvironmentType = environmentRow.querySelector('.inline-flex').textContent.trim()
    
    const currentEnvironmentName = this.activeEnvironmentTarget.textContent.trim()
    const currentEnvironmentType = document.querySelector('[data-environment-id] .inline-flex')?.textContent?.trim() || 'Unknown'

    this.showConfirmation('switch', {
      environmentId,
      currentEnvironment: currentEnvironmentName,
      currentEnvironmentType,
      newEnvironment: newEnvironmentName,
      newEnvironmentType,
      title: 'Switch Environment',
      message: 'This will change the active Salesforce environment for all operations.'
    })
  }

  async testConnection(event) {
    const environmentId = event.currentTarget.dataset.environmentId
    const statusTarget = this.getEnvironmentStatusTarget(environmentId)
    
    // Update status to testing
    this.updateConnectionStatus(statusTarget, 'testing', 'Testing')
    
    try {
      const response = await fetch(`/admin/salesforce/environments/${environmentId}/test_connection`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.getCSRFToken(),
          'Accept': 'application/json'
        }
      })

      const result = await response.json()

      if (response.ok && result.success) {
        this.updateConnectionStatus(statusTarget, 'connected', 'Connected')
        this.showNotification('success', 'Connection test successful')
      } else {
        this.updateConnectionStatus(statusTarget, 'error', 'Error')
        this.showNotification('error', result.error || 'Connection test failed')
      }
    } catch (error) {
      console.error('Connection test error:', error)
      this.updateConnectionStatus(statusTarget, 'error', 'Error')
      this.showNotification('error', 'Network error during connection test')
    }
  }

  // ========================================
  // Field Mappings Management
  // ========================================

  openFieldMappings(event) {
    console.log('Opening field mappings modal')
    const environmentId = event.currentTarget.dataset.environmentId
    const environmentRow = event.currentTarget.closest('tr')
    const environmentName = environmentRow.querySelector('td .text-gray-900').textContent.trim()
    
    console.log('Environment ID:', environmentId)
    console.log('Environment Name:', environmentName)
    
    this.currentEnvironmentId = environmentId
    
    // Check if targets exist
    if (!this.hasFieldMappingsModalTarget) {
      console.error('Field mappings modal target not found')
      return
    }
    
    if (this.hasMappingEnvironmentNameTarget) {
      this.mappingEnvironmentNameTarget.textContent = `- ${environmentName}`
    }
    
    this.loadFieldMappings(environmentId)
    this.showModal(this.fieldMappingsModalTarget)
  }

  async loadFieldMappings(environmentId) {
    try {
      const response = await fetch(`/admin/salesforce/environments/${environmentId}/field-mappings`, {
        headers: {
          'Accept': 'application/json'
        }
      })

      const result = await response.json()

      if (response.ok) {
        this.currentMappings = result.mappings || []
        this.renderMappings()
        this.updateActiveMappingsCount()
      } else {
        this.showNotification('error', 'Failed to load field mappings')
      }
    } catch (error) {
      console.error('Load mappings error:', error)
      this.showNotification('error', 'Network error while loading mappings')
    }
  }

  addMapping() {
    const newMapping = {
      id: `new_${Date.now()}`,
      application_field: '',
      salesforce_field: '',
      field_type: 'direct',
      transform_function: '',
      is_active: true
    }
    
    this.currentMappings.push(newMapping)
    this.renderMappings()
    this.updateActiveMappingsCount()
  }

  removeMapping(event) {
    const row = event.currentTarget.closest('tr')
    const mappingId = row.dataset.mappingId
    
    this.currentMappings = this.currentMappings.filter(mapping => mapping.id !== mappingId)
    this.renderMappings()
    this.updateActiveMappingsCount()
  }

  async loadDefaultMappings() {
    try {
      const response = await fetch('/admin/salesforce/default_mappings', {
        headers: {
          'Accept': 'application/json'
        }
      })

      const result = await response.json()

      if (response.ok) {
        // Merge default mappings with existing ones
        const existingFields = this.currentMappings.map(m => m.application_field)
        const newMappings = result.mappings.filter(mapping => 
          !existingFields.includes(mapping.application_field)
        )
        
        this.currentMappings = this.currentMappings.concat(newMappings)
        this.renderMappings()
        this.updateActiveMappingsCount()
        this.showNotification('success', `Added ${newMappings.length} default mappings`)
      } else {
        this.showNotification('error', 'Failed to load default mappings')
      }
    } catch (error) {
      console.error('Load default mappings error:', error)
      this.showNotification('error', 'Network error while loading default mappings')
    }
  }

  async validateMappings() {
    if (this.currentMappings.length === 0) {
      this.showNotification('warning', 'No mappings to validate')
      return
    }

    try {
      const response = await fetch(`/admin/salesforce/environments/${this.currentEnvironmentId}/validate_mappings`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.getCSRFToken(),
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ mappings: this.currentMappings })
      })

      const result = await response.json()

      if (response.ok) {
        this.displayValidationResults(result.validations)
      } else {
        this.showNotification('error', 'Validation failed')
      }
    } catch (error) {
      console.error('Validation error:', error)
      this.showNotification('error', 'Network error during validation')
    }
  }

  async saveMappings() {
    this.setLoading(this.saveMappingsButtonTarget, this.saveMappingsSpinnerTarget, this.saveMappingsButtonTextTarget, "Saving...")

    try {
      const response = await fetch(`/admin/salesforce/environments/${this.currentEnvironmentId}/field-mappings`, {
        method: 'PUT',
        headers: {
          'X-CSRF-Token': this.getCSRFToken(),
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ mappings: this.currentMappings })
      })

      const result = await response.json()

      if (response.ok) {
        this.showNotification('success', 'Field mappings saved successfully')
        this.closeFieldMappingsModal()
      } else {
        this.showNotification('error', result.error || 'Failed to save mappings')
      }
    } catch (error) {
      console.error('Save mappings error:', error)
      this.showNotification('error', 'Network error while saving mappings')
    } finally {
      this.clearLoading(this.saveMappingsButtonTarget, this.saveMappingsSpinnerTarget, this.saveMappingsButtonTextTarget, "Save Mappings")
    }
  }

  // ========================================
  // Import/Export Functionality
  // ========================================

  openImportModal() {
    console.log("Opening import modal...")
    console.log("Has importModal target:", this.hasImportModalTarget)
    console.log("Has previewButton target:", this.hasPreviewButtonTarget)
    
    this.resetImportForm()
    this.clearImportErrors()
    
    if (this.hasImportModalTarget) {
      console.log("Using target to show modal")
      this.showModal(this.importModalTarget)
    } else {
      console.error("Import modal target not found, using fallback")
      // Fallback to direct DOM manipulation
      const modal = document.getElementById('import-modal')
      if (modal) {
        console.log("Found modal by ID, showing...")
        this.showModalByElement(modal)
      } else {
        console.error("Modal not found by ID either!")
        // Last resort - show notification
        this.showNotification('error', 'Import modal not found. Please refresh the page.')
      }
    }
    
    // Re-initialize drop zone after modal opens and check button connections
    setTimeout(() => {
      this.initializeDropZone()
      this.debugButtonConnections()
      // Ensure file handling is properly initialized
      if (this.selectedFile) {
        this.enablePreviewButton()
        this.enableImportButton()
      }
    }, 100)
  }
  
  debugButtonConnections() {
    console.log("=== Debug Button Connections ===")
    const previewButton = document.querySelector('[data-salesforce-environments-target="previewButton"]')
    const importButton = document.querySelector('[data-salesforce-environments-target="importButton"]')
    
    console.log("Preview button:", previewButton)
    console.log("Import button:", importButton)
    
    if (previewButton) {
      console.log("Preview button disabled:", previewButton.disabled)
      console.log("Preview button classes:", previewButton.className)
      console.log("Preview button data-action:", previewButton.dataset.action)
      console.log("Stimulus controller connected:", this)
      console.log("Has preview button target:", this.hasPreviewButtonTarget)
      
      // Add a temporary test click handler to verify it's working
      const testHandler = (e) => {
        console.log('Test click handler triggered!', e)
        this.showNotification('info', 'Button click detected!')
      }
      
      previewButton.addEventListener('click', testHandler, { once: true })
      console.log('Test click handler added to preview button')
    }
  }
  
  // Test method to verify Preview button is working
  testPreviewButton() {
    console.log('=== Test Preview Button ===')
    this.showNotification('success', 'Preview button test successful!')
    return true
  }
  
  // Debug method to test import functionality
  debugImport() {
    console.log('=== Debug Import ===')
    console.log('Controller connected:', this.element)
    console.log('Has import form target:', this.hasImportFormTarget)
    console.log('Has import button target:', this.hasImportButtonTarget)
    console.log('Has import file target:', this.hasImportFileTarget)
    console.log('Selected file:', this.selectedFile)
    
    const form = this.hasImportFormTarget ? this.importFormTarget : document.querySelector('[data-salesforce-environments-target="importForm"]')
    console.log('Import form found:', form)
    
    if (form) {
      console.log('Form action attribute:', form.action)
      console.log('Form data-action:', form.dataset.action)
      console.log('Form enctype:', form.enctype)
    }
    
    // Test if we can manually trigger the import
    const importButton = this.hasImportButtonTarget ? this.importButtonTarget : document.querySelector('[data-salesforce-environments-target="importButton"]')
    if (importButton) {
      console.log('Import button found:', importButton)
      console.log('Import button disabled:', importButton.disabled)
      console.log('Import button data-action:', importButton.dataset.action)
      console.log('Import button form:', importButton.closest('form'))
    }
    
    this.showNotification('info', 'Import debug completed - check console')
    return true
  }
  
  // Test method to manually trigger import
  testImportButton() {
    console.log('=== Test Import Button ===')
    this.showNotification('info', 'Testing import button...')
    
    // Create a fake form submit event
    const form = this.hasImportFormTarget ? this.importFormTarget : document.querySelector('[data-salesforce-environments-target="importForm"]')
    if (form) {
      const event = new Event('submit', { bubbles: true, cancelable: true })
      form.dispatchEvent(event)
    } else {
      this.showNotification('error', 'Import form not found!')
    }
    
    return true
  }

  initializeDropZone() {
    // This method is now optional since we use data-action attributes on the drop zone
    // But keeping for backwards compatibility
    console.log('Drop zone initialized via Stimulus data-actions')
  }

  // Trigger file input click when clicking on drop zone
  triggerFileInput(event) {
    event.preventDefault()
    event.stopPropagation()
    console.log('=== Triggering file input ===')

    const fileInput = this.hasImportFileTarget ?
      this.importFileTarget :
      document.getElementById('import-modal-file-input')

    if (fileInput) {
      console.log('File input found, clicking...')
      fileInput.click()
    } else {
      console.error('File input not found!')
      this.showNotification('error', 'File input not found. Please refresh the page.')
    }
  }

  // Handle drag over event
  handleDragOver(event) {
    event.preventDefault()
    event.stopPropagation()

    const dropZone = event.currentTarget
    dropZone.classList.add('border-blue-400', 'bg-blue-50')
  }

  // Handle drag leave event
  handleDragLeave(event) {
    event.preventDefault()
    event.stopPropagation()

    const dropZone = event.currentTarget
    dropZone.classList.remove('border-blue-400', 'bg-blue-50')
  }

  // Handle file drop event
  handleFileDrop(event) {
    event.preventDefault()
    event.stopPropagation()

    const dropZone = event.currentTarget
    dropZone.classList.remove('border-blue-400', 'bg-blue-50')

    const files = event.dataTransfer.files
    console.log('Files dropped:', files.length)

    if (files.length > 0) {
      console.log('Processing dropped file:', files[0].name)
      this.handleFile(files[0])
    }
  }

  handleFileSelect(event) {
    console.log('=== File select event triggered ===', event)
    const file = event.target.files[0]
    if (file) {
      console.log('File selected from input:', file.name, file.size, file.type)
      this.handleFile(file)
    } else {
      console.log('No file selected from input')
      // Clear stored file if input is cleared
      this.selectedFile = null
      this.disablePreviewButton()
      this.disableImportButton()
    }
  }

  handleFile(file) {
    console.log('=== Handling file ===', file.name, file.size)
    
    // Clear any previous errors when a new file is selected
    this.clearImportErrors()
    
    // Validate file type
    if (!file.name.endsWith('.json')) {
      this.showNotification('error', 'Please select a JSON file')
      return
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      this.showNotification('error', 'File size must be less than 10MB')
      return
    }

    // Store the file for later use
    this.selectedFile = file
    console.log('File stored in selectedFile:', this.selectedFile)

    // Display file info with fallback
    if (this.hasFileNameTarget) {
      this.fileNameTarget.textContent = file.name
      console.log('File name displayed:', file.name)
    }
    if (this.hasFileSizeTarget) {
      this.fileSizeTarget.textContent = this.formatFileSize(file.size)
      console.log('File size displayed:', this.formatFileSize(file.size))
    }
    if (this.hasSelectedFileInfoTarget) {
      this.selectedFileInfoTarget.classList.remove('hidden')
      console.log('File info section shown')
    }

    // Enable buttons with improved logic
    console.log('Enabling buttons...')
    this.enablePreviewButton()
    this.enableImportButton()
    console.log('Buttons enabled')
  }

  enablePreviewButton() {
    const previewButton = this.hasPreviewButtonTarget ? 
      this.previewButtonTarget : 
      document.querySelector('[data-salesforce-environments-target="previewButton"]')
    
    console.log('Enabling preview button:', previewButton)
    
    if (previewButton) {
      previewButton.disabled = false
      previewButton.classList.remove('bg-gray-300', 'text-gray-500', 'cursor-not-allowed', 'disabled:opacity-50')
      previewButton.classList.add('bg-yellow-600', 'text-white', 'hover:bg-yellow-700', 'focus:ring-2', 'focus:ring-yellow-500', 'focus:ring-offset-2')
      
      // Ensure the data-action attribute is properly set
      if (!previewButton.dataset.action) {
        previewButton.dataset.action = 'click->salesforce-environments#previewImport'
      }
      
      console.log('Preview button enabled successfully, data-action:', previewButton.dataset.action)
    } else {
      console.error('Preview button not found!')
    }
  }

  enableImportButton() {
    const importButton = this.hasImportButtonTarget ? 
      this.importButtonTarget : 
      document.querySelector('[data-salesforce-environments-target="importButton"]')
    
    if (importButton) {
      importButton.disabled = false
      importButton.classList.remove('bg-gray-300', 'text-gray-500', 'cursor-not-allowed', 'disabled:opacity-50')
      importButton.classList.add('bg-green-600', 'text-white', 'hover:bg-green-700', 'focus:ring-2', 'focus:ring-green-500', 'focus:ring-offset-2')
    }
  }

  disablePreviewButton() {
    const previewButton = this.hasPreviewButtonTarget ? 
      this.previewButtonTarget : 
      document.querySelector('[data-salesforce-environments-target="previewButton"]')
    
    if (previewButton) {
      previewButton.disabled = true
      previewButton.classList.remove('bg-yellow-600', 'text-white', 'hover:bg-yellow-700', 'focus:ring-2', 'focus:ring-yellow-500', 'focus:ring-offset-2')
      previewButton.classList.add('bg-gray-300', 'text-gray-500', 'cursor-not-allowed', 'disabled:opacity-50')
    }
  }

  disableImportButton() {
    const importButton = this.hasImportButtonTarget ? 
      this.importButtonTarget : 
      document.querySelector('[data-salesforce-environments-target="importButton"]')
    
    if (importButton) {
      importButton.disabled = true
      importButton.classList.remove('bg-green-600', 'text-white', 'hover:bg-green-700', 'focus:ring-2', 'focus:ring-green-500', 'focus:ring-offset-2')
      importButton.classList.add('bg-gray-300', 'text-gray-500', 'cursor-not-allowed', 'disabled:opacity-50')
    }
  }

  removeSelectedFile() {
    console.log("Removing selected file...")
    
    // Clear the stored file reference
    this.selectedFile = null
    
    // Clear file input with null safety
    const fileInput = this.hasImportFileTarget ? 
      this.importFileTarget : 
      document.getElementById('import_file')
    
    if (fileInput && fileInput.value !== undefined) {
      try {
        fileInput.value = ''
      } catch (error) {
        console.warn("Could not clear file input:", error)
      }
    }
    
    // Hide file info and preview sections with null safety
    this.hideElementSafely('selectedFileInfo')
    this.hideElementSafely('previewSection')
    this.hideElementSafely('validationResults')
    
    // Clear file display info with null safety
    this.clearElementTextSafely('fileName')
    this.clearElementTextSafely('fileSize')
    
    // Disable buttons
    this.disablePreviewButton()
    this.disableImportButton()
  }

  // Helper method to safely hide elements
  hideElementSafely(targetName) {
    const element = this.hasTarget(targetName) ? 
      this[`${targetName}Target`] : 
      document.querySelector(`[data-salesforce-environments-target="${targetName}"]`)
    
    if (element && element.classList) {
      element.classList.add('hidden')
    }
  }

  // Helper method to safely clear element text content
  clearElementTextSafely(targetName) {
    const element = this.hasTarget(targetName) ? 
      this[`${targetName}Target`] : 
      document.querySelector(`[data-salesforce-environments-target="${targetName}"]`)
    
    if (element && typeof element.textContent !== 'undefined') {
      element.textContent = ''
    }
  }

  async previewImport(event) {
    console.log("=== Preview import clicked ===", event)
    
    // Prevent any default behavior
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    
    // Check if button is disabled
    const button = event?.currentTarget
    if (button && button.disabled) {
      console.log('Preview button is disabled, ignoring click')
      this.showNotification('warning', 'Please select a file first')
      return
    }
    
    console.log('Preview button is enabled, proceeding...')
    this.showNotification('info', 'Loading file preview...')
    
    // Get the file - first try stored file, then file input
    let file = this.selectedFile
    console.log('Stored file:', file)
    
    if (!file) {
      console.log('No stored file, checking file input...')
      const fileInput = this.hasImportFileTarget ? 
        this.importFileTarget : 
        document.getElementById('import_file')
      
      console.log('File input found:', fileInput)
      console.log('File input has files:', fileInput ? fileInput.files.length : 'N/A')
      
      if (!fileInput) {
        console.error('Import file target not found')
        this.showNotification('error', 'File input not found')
        return
      }
      
      file = fileInput.files[0]
      console.log('File from input:', file)
    }
    
    console.log('Selected file for preview:', file, file ? file.name : 'null')
    
    if (!file) {
      this.showNotification('error', 'Please select a file first')
      return
    }

    try {
      console.log('Reading file content...')
      const text = await file.text()
      console.log('File content read, parsing JSON...')
      const config = JSON.parse(text)
      console.log('JSON parsed successfully:', config)
      
      // Use fallback for preview content
      const previewContent = this.hasPreviewContentTarget ? 
        this.previewContentTarget : 
        document.querySelector('[data-salesforce-environments-target="previewContent"]')
      
      console.log('Preview content element:', previewContent)
      
      if (previewContent) {
        const formattedJSON = JSON.stringify(config, null, 2)
        previewContent.textContent = formattedJSON
        console.log('Preview content updated with', formattedJSON.length, 'characters')
      } else {
        console.error('Preview content element not found')
        console.log('Available elements with previewContent target:', document.querySelectorAll('[data-salesforce-environments-target="previewContent"]'))
      }
      
      // Use fallback for preview section
      const previewSection = this.hasPreviewSectionTarget ? 
        this.previewSectionTarget : 
        document.querySelector('[data-salesforce-environments-target="previewSection"]')
      
      console.log('Preview section element:', previewSection)
      
      if (previewSection) {
        previewSection.classList.remove('hidden')
        previewSection.style.display = 'block'  // Force display
        console.log('Preview section shown, classes:', previewSection.className)
      } else {
        console.error('Preview section element not found')
        console.log('Available elements with previewSection target:', document.querySelectorAll('[data-salesforce-environments-target="previewSection"]'))
      }
      
      // Basic validation if checkbox is checked
      const validateCheckbox = this.hasValidateOnImportTarget ? 
        this.validateOnImportTarget : 
        document.querySelector('[data-salesforce-environments-target="validateOnImport"]')
      
      if (validateCheckbox && validateCheckbox.checked) {
        console.log('Running validation...')
        this.validateImportConfig(config)
      }
      
      this.showNotification('success', 'File preview loaded successfully')
      
      // Scroll preview section into view
      if (previewSection) {
        previewSection.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    } catch (error) {
      console.error('Preview error:', error)
      if (error instanceof SyntaxError) {
        this.showNotification('error', 'Invalid JSON file format - please check your file syntax')
      } else {
        this.showNotification('error', `Failed to preview file: ${error.message}`)
      }
    }
  }

  validateImportConfig(config) {
    console.log('Validating import config:', config)
    
    const validationResults = []
    
    // Check if config has required structure
    if (!config || typeof config !== 'object') {
      validationResults.push({ level: 'error', message: 'Invalid configuration format' })
      return validationResults
    }
    
    // Determine if this is a single environment or multiple environments
    let environments = []
    
    if (config.environments && Array.isArray(config.environments)) {
      // Multiple environments format: { "environments": [...] }
      environments = config.environments
    } else if (config.name && config.environment_type) {
      // Single environment format: { "name": "...", "environment_type": "..." }
      environments = [config]
    } else {
      validationResults.push({ level: 'warning', message: 'No valid environments found in configuration' })
      return validationResults
    }
    
    let hasCredentials = false
    let missingCredentials = 0
    
    // Validate each environment
    environments.forEach((env, index) => {
      const envLabel = environments.length === 1 ? 'Environment' : `Environment ${index + 1}`
      
      if (!env.name) {
        validationResults.push({ level: 'error', message: `${envLabel}: Missing name` })
      }
      if (!env.environment_type) {
        validationResults.push({ level: 'warning', message: `${envLabel}: Missing type` })
      }
      if (env.environment_type && !['sandbox', 'production', 'development'].includes(env.environment_type.toLowerCase())) {
        validationResults.push({ level: 'warning', message: `${envLabel}: Unknown environment type "${env.environment_type}"` })
      }
      
      // Check for credentials
      const credentialFields = ['client_id', 'client_secret', 'username', 'password', 'security_token']
      const presentCredentials = credentialFields.filter(field => env[field] && env[field].trim())
      
      if (presentCredentials.length > 0) {
        hasCredentials = true
      } else {
        missingCredentials++
      }
    })
    
    // Provide informative messages about what will happen
    if (missingCredentials > 0 && missingCredentials === environments.length) {
      validationResults.push({ 
        level: 'info', 
        message: `All ${environments.length} environment(s) are missing credentials - this is normal for exported configurations. You'll add credentials after import.`
      })
    } else if (missingCredentials > 0) {
      validationResults.push({ 
        level: 'info', 
        message: `${missingCredentials} of ${environments.length} environment(s) are missing credentials - you'll add these after import.`
      })
    }
    
    // Add success message if structure is valid
    if (validationResults.filter(r => r.level === 'error').length === 0) {
      const envCount = environments.length
      const envText = envCount === 1 ? '1 environment' : `${envCount} environments`
      validationResults.push({ 
        level: 'success', 
        message: `Configuration structure is valid - ready to import ${envText}`
      })
    }
    
    // Display validation results
    if (validationResults.length > 0) {
      if (this.hasValidationResultsTarget) {
        this.validationResultsTarget.classList.remove('hidden')
        
        const content = validationResults.map(result => {
          let bgColor, textColor, icon
          
          if (result.level === 'error') {
            bgColor = 'bg-red-50 border-red-200'
            textColor = 'text-red-700'
            icon = `<path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>`
          } else if (result.level === 'warning') {
            bgColor = 'bg-yellow-50 border-yellow-200'
            textColor = 'text-yellow-700'
            icon = `<path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>`
          } else if (result.level === 'info') {
            bgColor = 'bg-blue-50 border-blue-200'
            textColor = 'text-blue-700'
            icon = `<path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>`
          } else {
            bgColor = 'bg-green-50 border-green-200'
            textColor = 'text-green-700'
            icon = `<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>`
          }
          
          return `<div class="flex items-start p-3 rounded border ${bgColor} ${textColor}">
            <svg class="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              ${icon}
            </svg>
            <div class="text-sm">${result.message}</div>
          </div>`
        }).join('')
        
        if (this.hasValidationContentTarget) {
          this.validationContentTarget.innerHTML = content
        }
      }
      
      // Show appropriate notification
      const hasErrors = validationResults.some(r => r.level === 'error')
      const hasWarnings = validationResults.some(r => r.level === 'warning')
      const hasSuccess = validationResults.some(r => r.level === 'success')
      
      if (hasErrors) {
        this.showNotification('error', 'Configuration has validation errors')
      } else if (hasWarnings) {
        this.showNotification('warning', 'Configuration has warnings but can be imported')
      } else if (hasSuccess) {
        this.showNotification('success', 'Configuration looks good - ready to import')
      }
    }
    
    return validationResults
  }

  async processImport(event) {
    console.log("=== Starting processImport ===", event)
    console.log("Event type:", event.type)
    console.log("Event target:", event.target)
    console.log("Current target:", event.currentTarget)
    
    // Prevent default behavior for both click and submit events
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    
    // Check if button is disabled (important for click events)
    const button = event?.currentTarget
    if (button && button.disabled) {
      console.log('Import button is disabled, ignoring click')
      this.showNotification('warning', 'Please select a file first')
      return
    }
    
    // Add immediate user feedback
    this.showNotification('info', 'Processing import request...')
    
    // Try stored file first, then file input
    let file = this.selectedFile
    if (!file) {
      const fileInput = this.hasImportFileTarget ? this.importFileTarget : document.getElementById('import_file')
      file = fileInput ? fileInput.files[0] : null
    }
    
    console.log("File input found:", this.hasImportFileTarget ? this.importFileTarget : document.getElementById('import_file'))
    console.log("Selected file:", file)
    console.log("Selected file from property:", this.selectedFile)
    
    if (!file) {
      console.error("No file selected for import")
      console.error("Debug info:", {
        hasStoredFile: !!this.selectedFile,
        hasFileInput: !!(this.hasImportFileTarget ? this.importFileTarget : document.getElementById('import_file')),
        fileInputValue: (this.hasImportFileTarget ? this.importFileTarget : document.getElementById('import_file'))?.files?.length || 0,
        modalId: this.element.closest('[id]')?.id || 'no-modal-id',
        controllerElement: this.element.tagName
      })
      this.showNotification('error', 'Please select a file to import')
      return
    }

    // Use fallback elements
    const importButton = this.hasImportButtonTarget ? this.importButtonTarget : document.querySelector('[data-salesforce-environments-target="importButton"]')
    const importSpinner = this.hasImportSpinnerTarget ? this.importSpinnerTarget : document.querySelector('[data-salesforce-environments-target="importSpinner"]')
    const importButtonText = this.hasImportButtonTextTarget ? this.importButtonTextTarget : document.querySelector('[data-salesforce-environments-target="importButtonText"]')
    const progressSection = this.hasProgressSectionTarget ? this.progressSectionTarget : document.querySelector('[data-salesforce-environments-target="progressSection"]')
    
    console.log("UI elements found:", {
      importButton: !!importButton,
      importSpinner: !!importSpinner, 
      importButtonText: !!importButtonText,
      progressSection: !!progressSection
    })
    
    this.setLoading(importButton, importSpinner, importButtonText, "Importing...")
    if (progressSection) {
      progressSection.classList.remove('hidden')
    }
    
    this.showNotification('info', 'Starting import process...')

    try {
      const formData = new FormData()
      formData.append('import_file', file)
      
      // Use fallback checkboxes
      const overwriteExisting = this.hasOverwriteExistingTarget ? this.overwriteExistingTarget : document.querySelector('[data-salesforce-environments-target="overwriteExisting"]')
      const importFieldMappings = this.hasImportFieldMappingsTarget ? this.importFieldMappingsTarget : document.querySelector('[data-salesforce-environments-target="importFieldMappings"]')
      const validateOnImport = this.hasValidateOnImportTarget ? this.validateOnImportTarget : document.querySelector('[data-salesforce-environments-target="validateOnImport"]')
      const testConnections = this.hasTestConnectionsTarget ? this.testConnectionsTarget : document.querySelector('[data-salesforce-environments-target="testConnections"]')
      
      console.log("Checkbox elements found:", {
        overwriteExisting: !!overwriteExisting,
        importFieldMappings: !!importFieldMappings,
        validateOnImport: !!validateOnImport,
        testConnections: !!testConnections
      })
      
      // Append checkbox values as strings (server expects "true"/"false")
      formData.append('overwrite_existing', overwriteExisting ? overwriteExisting.checked.toString() : 'false')
      formData.append('import_field_mappings', importFieldMappings ? importFieldMappings.checked.toString() : 'true')
      formData.append('validate_on_import', validateOnImport ? validateOnImport.checked.toString() : 'true') 
      formData.append('test_connections', testConnections ? testConnections.checked.toString() : 'false')

      console.log("Form data prepared with options:", {
        overwrite_existing: formData.get('overwrite_existing'),
        import_field_mappings: formData.get('import_field_mappings'),
        validate_on_import: formData.get('validate_on_import'),
        test_connections: formData.get('test_connections')
      })

      // Construct URL more robustly
      const baseUrl = window.location.pathname.includes('/admin/salesforce') 
        ? '/admin/salesforce/environments/import_config' 
        : '/admin/salesforce_environments/import_config'
      console.log("Making request to:", baseUrl)

      console.log("CSRF Token:", this.getCSRFToken())
      console.log("FormData entries:")
      for (let pair of formData.entries()) {
        console.log(`  ${pair[0]}:`, pair[1])
      }

      const response = await fetch(baseUrl, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.getCSRFToken(),
          'Accept': 'application/json'
        },
        body: formData
      })

      console.log("Response received:", response.status, response.statusText)
      console.log("Response headers:", Object.fromEntries(response.headers.entries()))

      let result
      try {
        const responseText = await response.text()
        console.log("Raw response text:", responseText)
        result = JSON.parse(responseText)
        console.log("Parsed response data:", result)
      } catch (parseError) {
        console.error("Failed to parse JSON response:", parseError)
        throw new Error("Invalid JSON response from server")
      }

      if (response.ok && result.success) {
        let message = result.message || 'Configuration imported successfully'
        
        // Add additional details if available
        if (result.warnings && result.warnings.length > 0) {
          message += ` (${result.warnings.length} warning(s))`
        }
        
        if (result.connection_test_results) {
          const testResults = Object.values(result.connection_test_results)
          const successful = testResults.filter(r => r.success).length
          const total = testResults.length
          if (total > 0) {
            message += `. Connection tests: ${successful}/${total} successful`
          }
        }
        
        this.showNotification('success', message)
        
        // Show warnings if any
        if (result.warnings && result.warnings.length > 0) {
          result.warnings.forEach(warning => {
            this.showNotification('warning', warning)
          })
        }
        
        this.closeImportModal()
        this.refreshEnvironmentsList()
      } else {
        // Handle different types of error responses
        let errorMessage = 'Import failed'
        
        if (result.error) {
          errorMessage = result.error
        } else if (result.errors && Array.isArray(result.errors)) {
          errorMessage = result.errors.join(', ')
        } else if (result.message) {
          errorMessage = result.message
        }
        
        console.error('Import failed:', errorMessage)
        console.error('Response status:', response.status, response.statusText)
        console.error('Full response:', result)
        
        // Show detailed validation error with proper status handling
        this.showImportError(errorMessage, result, response.status)
      }
    } catch (error) {
      console.error('Import error:', error)
      this.showNotification('error', 'Network error during import: ' + error.message)
    } finally {
      this.clearLoading(importButton, importSpinner, importButtonText, "Import Configuration")
      if (progressSection) {
        progressSection.classList.add('hidden')
      }
    }
  }

  async exportConfigurations() {
    try {
      const response = await get('/admin/salesforce/environments/export_all.json', {
        responseKind: 'json'
      })

      if (response.ok) {
        const blob = await response.response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `salesforce-environments-${new Date().toISOString().split('T')[0]}.json`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)

        this.showNotification('success', 'Configuration exported successfully')
      } else {
        this.showNotification('error', 'Export failed')
      }
    } catch (error) {
      console.error('Export error:', error)
      this.showNotification('error', 'Network error during export')
    }
  }

  // ========================================
  // Filtering and Search
  // ========================================

  filterByType() {
    const typeFilter = this.typeFilterTarget.value
    const statusFilter = this.statusFilterTarget.value
    this.applyFilters(typeFilter, statusFilter)
  }

  filterByStatus() {
    const typeFilter = this.typeFilterTarget.value
    const statusFilter = this.statusFilterTarget.value
    this.applyFilters(typeFilter, statusFilter)
  }

  applyFilters(typeFilter, statusFilter) {
    const rows = this.environmentsListTarget.querySelectorAll('tbody tr')
    
    rows.forEach(row => {
      const environmentType = row.querySelector('.inline-flex')?.textContent?.toLowerCase()
      const connectionStatus = row.querySelector('[data-environment-id]')?.textContent?.toLowerCase()
      
      let showRow = true
      
      if (typeFilter && environmentType && !environmentType.includes(typeFilter)) {
        showRow = false
      }
      
      if (statusFilter && connectionStatus && !connectionStatus.includes(statusFilter)) {
        showRow = false
      }
      
      row.style.display = showRow ? '' : 'none'
    })
  }

  // ========================================
  // Bulk Operations
  // ========================================

  async bulkTestConnections() {
    try {
      this.showNotification('info', 'Testing all active environment connections...')
      
      const response = await fetch('/admin/salesforce/environments/bulk_test_connections', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.getCSRFToken(),
          'Accept': 'application/json'
        }
      })

      const result = await response.json()

      if (response.ok) {
        const { results } = result
        let successCount = 0
        let failureCount = 0
        
        Object.entries(results).forEach(([envId, testResult]) => {
          const statusTarget = this.getEnvironmentStatusTarget(envId)
          if (testResult.success) {
            this.updateConnectionStatus(statusTarget, 'connected', 'Connected')
            successCount++
          } else {
            this.updateConnectionStatus(statusTarget, 'error', 'Error')
            failureCount++
          }
        })
        
        this.showNotification('success', `Tested ${successCount + failureCount} environments: ${successCount} successful, ${failureCount} failed`)
      } else {
        this.showNotification('error', 'Failed to test connections')
      }
    } catch (error) {
      console.error('Bulk test connections error:', error)
      this.showNotification('error', 'Network error during bulk test')
    }
  }

  // ========================================
  // Modal Management
  // ========================================

  showModal(modalTarget) {
    console.log("Showing modal:", modalTarget)
    if (modalTarget) {
      this.showModalByElement(modalTarget)
    } else {
      console.error("Modal target not found")
    }
  }

  showModalByElement(modalElement) {
    console.log("Showing modal element:", modalElement)
    if (modalElement) {
      // Remove hidden class and set display
      modalElement.classList.remove('hidden')
      modalElement.style.display = 'block'
      modalElement.style.zIndex = '50'
      modalElement.style.position = 'fixed'
      modalElement.style.inset = '0'
      
      // Add body overflow hidden and modal open class
      document.body.classList.add('overflow-hidden', 'modal-open')
      
      // Force reflow to ensure the modal is properly rendered
      modalElement.offsetHeight
      
      // Add opacity transition for smooth appearance
      setTimeout(() => {
        modalElement.style.opacity = '1'
      }, 10)
    } else {
      console.error("Modal element not found")
    }
  }

  closeModal() {
    this.environmentModalTarget.classList.add('hidden')
    document.body.classList.remove('overflow-hidden', 'modal-open')
    this.resetEnvironmentForm()
  }

  closeFieldMappingsModal() {
    this.fieldMappingsModalTarget.classList.add('hidden')
    document.body.classList.remove('overflow-hidden', 'modal-open')
  }

  closeImportModal() {
    console.log("Closing import modal...")
    const modal = this.hasImportModalTarget ? this.importModalTarget : document.getElementById('import-modal')
    
    if (modal) {
      modal.style.opacity = '0'
      setTimeout(() => {
        modal.classList.add('hidden')
        modal.style.display = 'none'
        modal.style.overflow = ''
      }, 150)
    }
    
    document.body.classList.remove('overflow-hidden', 'modal-open')
    document.body.style.overflow = ''
    this.resetImportForm()
  }

  closeConfirmationModal() {
    this.confirmationModalTarget.classList.add('hidden')
    document.body.classList.remove('overflow-hidden', 'modal-open')
    this.pendingAction = null
  }

  // ========================================
  // Utility Methods
  // ========================================

  resetEnvironmentForm() {
    this.environmentFormTarget.reset()
    this.environmentIdTarget.value = ''
  }

  resetImportForm() {
    console.log("Resetting import form...")
    
    // Clear stored file reference
    this.selectedFile = null
    
    // Reset form if available
    const importForm = this.hasImportFormTarget ? 
      this.importFormTarget : 
      document.querySelector('[data-salesforce-environments-target="importForm"]')
    
    if (importForm) {
      try {
        importForm.reset()
      } catch (error) {
        console.warn("Could not reset import form:", error)
      }
    }
    
    // Reset checkboxes to default values with null safety
    this.resetCheckboxSafely('overwriteExisting', false)
    this.resetCheckboxSafely('importFieldMappings', true)
    this.resetCheckboxSafely('validateOnImport', true)
    this.resetCheckboxSafely('testConnections', false)
    
    this.removeSelectedFile()
  }

  resetCheckboxSafely(targetName, defaultValue) {
    const element = this.hasTarget(targetName) ? 
      this[`${targetName}Target`] : 
      document.querySelector(`[data-salesforce-environments-target="${targetName}"]`)
    
    if (element && typeof element.checked !== 'undefined') {
      element.checked = defaultValue
    }
  }

  // Helper method to safely check if a target exists
  hasTarget(targetName) {
    const capitalizedTargetName = `${targetName}Target`
    try {
      return this[`has${capitalizedTargetName.charAt(0).toUpperCase() + capitalizedTargetName.slice(1)}`]
    } catch (error) {
      return false
    }
  }

  clearImportErrors() {
    console.log("Clearing import errors...")
    
    // Hide validation results
    const validationResults = this.hasValidationResultsTarget ? 
      this.validationResultsTarget : 
      document.querySelector('[data-salesforce-environments-target="validationResults"]')
    
    if (validationResults) {
      validationResults.classList.add('hidden')
    }
    
    // Clear validation content
    const validationContent = this.hasValidationContentTarget ? 
      this.validationContentTarget : 
      document.querySelector('[data-salesforce-environments-target="validationContent"]')
    
    if (validationContent) {
      validationContent.innerHTML = ''
    }
  }

  async loadEnvironmentData(environmentId) {
    try {
      const response = await fetch(`/admin/salesforce/environments/${environmentId}`, {
        headers: {
          'Accept': 'application/json'
        }
      })

      const result = await response.json()

      if (response.ok) {
        const env = result.environment
        this.environmentIdTarget.value = env.id
        this.environmentNameTarget.value = env.name
        this.environmentTypeTarget.value = env.environment_type
        this.environmentDescriptionTarget.value = env.description || ''
        this.clientIdTarget.value = env.client_id || ''
        this.usernameTarget.value = env.username || ''
        this.instanceUrlTarget.value = env.instance_url || ''
        this.isSandboxTarget.checked = env.is_sandbox || false
        this.autoSyncEnabledTarget.checked = env.auto_sync_enabled || false
        this.testOnSaveTarget.checked = true
        
        // Don't populate sensitive fields for security
        this.clientSecretTarget.value = ''
        this.passwordTarget.value = ''
        this.securityTokenTarget.value = ''
      }
    } catch (error) {
      console.error('Load environment data error:', error)
      this.showNotification('error', 'Failed to load environment data')
    }
  }

  showConfirmation(type, options) {
    this.pendingAction = { type, options }
    
    // Set title and message
    this.confirmationTitleTarget.textContent = options.title
    this.confirmationMessageTarget.textContent = options.message
    
    // Load appropriate template
    const template = this[`${type}EnvironmentTemplateTarget`]
    if (template) {
      let content = template.innerHTML
      Object.entries(options).forEach(([key, value]) => {
        content = content.replace(new RegExp(`data-placeholder="${key}"`, 'g'), `>${value}<`)
      })
      this.confirmationDetailsTarget.innerHTML = content
    }
    
    // Configure for destructive actions
    if (options.destructive) {
      this.destructiveWarningTarget.classList.remove('hidden')
      this.destructiveWarningTextTarget.textContent = 'This action cannot be undone.'
      
      if (options.confirmText) {
        this.confirmationInputTarget.classList.remove('hidden')
        this.confirmationInputValueTarget.textContent = options.confirmText
        this.confirmButtonTarget.disabled = true
      }
      
      this.confirmButtonTarget.classList.add('bg-red-600', 'hover:bg-red-700', 'focus:ring-red-500')
      this.confirmButtonTarget.classList.remove('bg-blue-600', 'hover:bg-blue-700', 'focus:ring-blue-500')
    } else {
      this.destructiveWarningTarget.classList.add('hidden')
      this.confirmationInputTarget.classList.add('hidden')
      this.confirmButtonTarget.disabled = false
      
      this.confirmButtonTarget.classList.add('bg-blue-600', 'hover:bg-blue-700', 'focus:ring-blue-500')
      this.confirmButtonTarget.classList.remove('bg-red-600', 'hover:bg-red-700', 'focus:ring-red-500')
    }
    
    this.showModal(this.confirmationModalTarget)
  }

  validateConfirmationInput() {
    if (!this.pendingAction?.options?.confirmText) return
    
    const input = this.confirmationInputFieldTarget.value
    const required = this.pendingAction.options.confirmText
    
    this.confirmButtonTarget.disabled = input !== required
  }

  async executeConfirmedAction() {
    if (!this.pendingAction) return

    const { type, options } = this.pendingAction
    
    this.setLoading(this.confirmButtonTarget, this.confirmSpinnerTarget, this.confirmButtonTextTarget, "Processing...")

    try {
      switch (type) {
        case 'delete':
          await this.executeDeleteEnvironment(options.environmentId)
          break
        case 'switch':
          await this.executeSwitchEnvironment(options.environmentId)
          break
        case 'test':
          await this.testEnvironmentConnection(options.environmentId)
          break
      }
    } finally {
      this.clearLoading(this.confirmButtonTarget, this.confirmSpinnerTarget, this.confirmButtonTextTarget, "Confirm")
      this.closeConfirmationModal()
    }
  }

  async executeDeleteEnvironment(environmentId) {
    try {
      const response = await fetch(`/admin/salesforce/environments/${environmentId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': this.getCSRFToken(),
          'Accept': 'application/json'
        }
      })

      const result = await response.json()

      if (response.ok) {
        this.showNotification('success', 'Environment deleted successfully')
        this.refreshEnvironmentsList()
      } else {
        this.showNotification('error', result.error || 'Failed to delete environment')
      }
    } catch (error) {
      console.error('Delete environment error:', error)
      this.showNotification('error', 'Network error during deletion')
    }
  }

  async executeSwitchEnvironment(environmentId) {
    try {
      const response = await fetch(`/admin/salesforce/environments/${environmentId}/activate`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.getCSRFToken(),
          'Accept': 'application/json'
        }
      })

      const result = await response.json()

      if (response.ok) {
        this.showNotification('success', 'Environment switched successfully')
        this.refreshEnvironmentsList()
        
        // Update active environment display
        this.activeEnvironmentTarget.textContent = result.environment_name
      } else {
        this.showNotification('error', result.error || 'Failed to switch environment')
      }
    } catch (error) {
      console.error('Switch environment error:', error)
      this.showNotification('error', 'Network error during switch')
    }
  }

  async testEnvironmentConnection(environmentId) {
    const statusTarget = this.getEnvironmentStatusTarget(environmentId)
    this.updateConnectionStatus(statusTarget, 'testing', 'Testing')
    
    try {
      const response = await fetch(`/admin/salesforce/environments/${environmentId}/test_connection`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.getCSRFToken(),
          'Accept': 'application/json'
        }
      })

      const result = await response.json()

      if (response.ok && result.success) {
        this.updateConnectionStatus(statusTarget, 'connected', 'Connected')
        this.showNotification('success', 'Connection test successful')
      } else {
        this.updateConnectionStatus(statusTarget, 'error', 'Error')
        this.showNotification('error', result.error || 'Connection test failed')
      }
    } catch (error) {
      console.error('Connection test error:', error)
      this.updateConnectionStatus(statusTarget, 'error', 'Error')
      this.showNotification('error', 'Network error during connection test')
    }
  }

  togglePasswordVisibility(event) {
    const targetId = event.currentTarget.dataset.target
    const input = document.getElementById(targetId)
    
    if (input.type === 'password') {
      input.type = 'text'
    } else {
      input.type = 'password'
    }
  }

  updateConnectionStatus(statusTarget, status, text) {
    if (!statusTarget) return
    
    const span = statusTarget.querySelector('span')
    if (!span) return
    
    // Remove all status classes from span
    span.classList.remove('text-green-900', 'text-red-900', 'text-yellow-900', 'text-gray-900')
    
    // Add appropriate classes based on status
    switch (status) {
      case 'connected':
        span.classList.add('text-green-900')
        break
      case 'error':
        span.classList.add('text-red-900')
        break
      case 'testing':
        span.classList.add('text-yellow-900')
        break
      default:
        span.classList.add('text-gray-900')
    }
    
    span.textContent = text
  }

  getEnvironmentStatusTarget(environmentId) {
    return document.querySelector(`[data-salesforce-environments-target="environmentStatus"][data-environment-id="${environmentId}"]`)
  }

  renderMappings() {
    const tbody = this.mappingsTableTarget
    
    // Clear existing rows except empty row
    const existingRows = tbody.querySelectorAll('tr:not([data-salesforce-environments-target="emptyMappingsRow"])')
    existingRows.forEach(row => row.remove())
    
    if (this.currentMappings.length === 0) {
      this.emptyMappingsRowTarget.style.display = ''
    } else {
      this.emptyMappingsRowTarget.style.display = 'none'
      
      this.currentMappings.forEach(mapping => {
        const row = this.createMappingRow(mapping)
        tbody.appendChild(row)
      })
    }
  }

  createMappingRow(mapping) {
    const template = this.mappingRowTemplateTarget.innerHTML
    const div = document.createElement('div')
    div.innerHTML = template
    const row = div.querySelector('tr')
    
    row.dataset.mappingId = mapping.id
    
    // Populate form fields
    row.querySelector('[name="application_field"]').value = mapping.application_field || ''
    row.querySelector('[name="salesforce_field"]').value = mapping.salesforce_field || ''
    row.querySelector('[name="field_type"]').value = mapping.field_type || 'direct'
    row.querySelector('[name="transform_function"]').value = mapping.transform_function || ''
    row.querySelector('[name="is_active"]').checked = mapping.is_active !== false
    
    return row
  }

  updateActiveMappingsCount() {
    const activeCount = this.currentMappings.filter(m => m.is_active !== false).length
    this.activeMappingsCountTarget.textContent = activeCount
  }

  displayValidationResults(validations) {
    const warnings = validations.filter(v => v.level === 'warning')
    const errors = validations.filter(v => v.level === 'error')
    
    if (warnings.length > 0 || errors.length > 0) {
      this.validationWarningsTarget.style.display = 'block'
      const list = this.validationWarningsListTarget
      list.innerHTML = ''
      
      warnings.concat(errors).forEach(validation => {
        const li = document.createElement('li')
        li.textContent = validation.message
        li.className = validation.level === 'error' ? 'text-red-700' : 'text-yellow-700'
        list.appendChild(li)
      })
    } else {
      this.validationWarningsTarget.style.display = 'none'
      this.showNotification('success', 'All mappings are valid')
    }
  }

  async refreshEnvironmentsList() {
    try {
      const response = await fetch('/admin/salesforce_environments', {
        headers: {
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        // Reload the page content
        window.location.reload()
      }
    } catch (error) {
      console.error('Refresh environments error:', error)
    }
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  setLoading(button, spinner, textTarget, loadingText) {
    button.disabled = true
    spinner.classList.remove('hidden')
    textTarget.textContent = loadingText
  }

  clearLoading(button, spinner, textTarget, originalText) {
    button.disabled = false
    spinner.classList.add('hidden')
    textTarget.textContent = originalText
  }

  getCSRFToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  showImportError(errorMessage, result, statusCode = null) {
    // Handle missing credentials as expected behavior rather than an error
    const isMissingCredentials = this.detectMissingCredentials(errorMessage, result)
    
    const errorContainer = this.hasValidationResultsTarget ? 
      this.validationResultsTarget : 
      document.querySelector('[data-salesforce-environments-target="validationResults"]')
    
    if (errorContainer) {
      errorContainer.classList.remove('hidden')
      
      let content = `<div class="space-y-4">`
      
      if (isMissingCredentials) {
        // Show this as expected behavior, not an error
        content += this.createCredentialInformationPanel()
        content += this.createActionPanel()
      } else {
        // Show actual error for real problems
        content += this.createErrorPanel(errorMessage, statusCode)
        if (result.errors && Array.isArray(result.errors)) {
          content += this.createValidationDetailsPanel(result.errors)
        }
      }
      
      content += `</div>`
      
      const validationContent = this.hasValidationContentTarget ? 
        this.validationContentTarget : 
        document.querySelector('[data-salesforce-environments-target="validationContent"]')
      
      if (validationContent) {
        validationContent.innerHTML = content
      }
      
      // Scroll to show the information
      errorContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
    
    // Show appropriate notification
    if (isMissingCredentials) {
      this.showNotification('info', 'Configuration imported successfully - credentials required to activate')
    } else {
      const notificationMessage = statusCode === 422 ? 'Import validation failed' : 'Import failed'
      this.showNotification('error', notificationMessage)
    }
  }

  detectMissingCredentials(errorMessage, result) {
    // Detect if this is a missing credentials scenario
    const credentialErrors = [
      'can\'t be blank',
      'is too short',
      'must be a valid',
      'Client',
      'client_id',
      'client_secret',
      'username',
      'password',
      'security_token'
    ]
    
    const hasCredentialError = credentialErrors.some(keyword => 
      errorMessage.toLowerCase().includes(keyword.toLowerCase()) ||
      (result.errors && result.errors.some(error => 
        error.toLowerCase().includes(keyword.toLowerCase())
      ))
    )
    
    return hasCredentialError
  }

  createCredentialInformationPanel() {
    return `
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-lg font-medium text-blue-900 mb-2">Configuration Structure Imported Successfully</h3>
            <div class="text-sm text-blue-800 space-y-2">
              <p><strong>Good news!</strong> Your configuration file has been processed and the environment structure has been imported.</p>
              <p><strong>Next step:</strong> Add your Salesforce credentials to activate the environment. For security reasons, exported configurations don't include sensitive information like Client IDs and passwords.</p>
            </div>
          </div>
        </div>
      </div>
    `
  }

  createActionPanel() {
    return `
      <div class="bg-green-50 border border-green-200 rounded-lg p-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div class="ml-3">
            <h4 class="text-sm font-medium text-green-900 mb-2">Ready to Add Credentials</h4>
            <div class="text-sm text-green-800 mb-3">
              <p>Your environment structure is ready. You can now add your Salesforce credentials to make it functional.</p>
            </div>
            <div class="flex flex-col sm:flex-row gap-3">
              <button type="button" 
                      onclick="this.closest('[data-salesforce-environments-target=\\"importModal\\"]').querySelector('[data-action*=\\"closeImportModal\\"]').click(); setTimeout(() => { const newBtn = document.querySelector('a[href*=\\"/new\\"]'); if (newBtn) newBtn.click(); }, 200);"
                      class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors">
                <svg class="-ml-0.5 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Create New Environment
              </button>
              <button type="button" 
                      onclick="this.closest('[data-salesforce-environments-target=\\"importModal\\"]').querySelector('[data-action*=\\"closeImportModal\\"]').click();"
                      class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors">
                <svg class="-ml-0.5 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                View Environment List
              </button>
            </div>
          </div>
        </div>
      </div>
    `
  }

  createErrorPanel(errorMessage, statusCode) {
    const statusText = statusCode === 422 ? 'Validation Error' : statusCode === 500 ? 'Server Error' : 'Import Failed'
    return `
      <div class="bg-red-50 border border-red-200 rounded-lg p-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-900">${statusText}</h3>
            <div class="mt-1 text-sm text-red-800">${errorMessage}</div>
            ${statusCode ? `<div class="mt-1 text-xs text-red-600">Status Code: ${statusCode}</div>` : ''}
          </div>
        </div>
      </div>
    `
  }

  createValidationDetailsPanel(errors) {
    return `
      <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div class="ml-3">
            <h4 class="text-sm font-medium text-yellow-900 mb-2">Technical Details</h4>
            <div class="text-sm text-yellow-800">
              <details class="cursor-pointer">
                <summary class="font-medium hover:text-yellow-900">View validation details</summary>
                <ul class="mt-2 space-y-1 list-disc list-inside pl-4">
                  ${errors.map(error => `<li class="text-xs">${error}</li>`).join('')}
                </ul>
              </details>
            </div>
          </div>
        </div>
      </div>
    `
  }

  showNotification(type, message) {
    // Create notification element
    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 z-50 max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto`
    
    const colorClasses = {
      success: 'border-l-4 border-green-400',
      error: 'border-l-4 border-red-400',
      warning: 'border-l-4 border-yellow-400',
      info: 'border-l-4 border-blue-400'
    }
    
    notification.className += ` ${colorClasses[type] || colorClasses.info}`
    
    const iconColor = {
      success: 'text-green-400',
      error: 'text-red-400', 
      warning: 'text-yellow-400',
      info: 'text-blue-400'
    }
    
    // Use different icons for different types
    let iconPath = 'M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z' // success icon
    
    if (type === 'error') {
      iconPath = 'M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
    } else if (type === 'warning') {
      iconPath = 'M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
    } else if (type === 'info') {
      iconPath = 'M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
    }
    
    notification.innerHTML = `
      <div class="p-4">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 ${iconColor[type]}" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="${iconPath}" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3 w-0 flex-1">
            <p class="text-sm font-medium text-gray-900 capitalize">${type}</p>
            <p class="mt-1 text-sm text-gray-500">${message}</p>
          </div>
          <div class="ml-4 flex-shrink-0 flex">
            <button type="button" class="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none" onclick="this.closest('.fixed').remove()">
              <span class="sr-only">Close</span>
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    `
    
    document.body.appendChild(notification)
    
    // Auto-remove after longer time for errors to give users time to read
    const autoRemoveDelay = type === 'error' ? 8000 : 5000
    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove()
      }
    }, autoRemoveDelay)
  }
}